import { Injectable } from '@angular/core';
import { Deserialize } from 'cerialize';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CardType, PaymentAccount, PaymentMethod } from '../../models';
import { UzurvRestService } from '@uzurv/rideWss/http';
import { DateFormatConstants } from '@uzurv/rideWss/app/constants';

@Injectable({
  providedIn: 'root'
})
export class PaymentAccountService extends UzurvRestService {
  constructor() {
    super();
  }

  /**
   * Retrieves an array of payment methods available from the server.
   *
   * @return {Observable<Array<PaymentMethod>>} An Observable that emits an array of PaymentMethod objects.
   */
  getPaymentMethods(): Observable<Array<PaymentMethod>> {
    return this.get('paymentAccounts/paymentMethods/', {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        return Deserialize(response, PaymentMethod);
      })
    );
  }

  /**
   * Finds a payment method by its ID.
   *
   * @param {number} id - The ID of the payment method to find.
   * @return {Observable<PaymentMethod>} An Observable that emits the payment method found with the specified ID.
   */
  findPaymentMethodById(id: number): Observable<PaymentMethod> {
    return this.getPaymentMethods().pipe(map(response => response.find(paymentMethod => paymentMethod.id === id)));
  }

  /**
   * Find a payment method by name.
   *
   * @param {string} name - The name of the payment method to find.
   * @returns {Observable<PaymentMethod>} An Observable that emits the payment method with the specified name.
   */
  findPaymentMethodByName(name: string): Observable<PaymentMethod> {
    return this.getPaymentMethods().pipe(map(response => response.find(paymentMethod => paymentMethod.name === name)));
  }

  /**
   * Retrieves an array of card types from the server.
   *
   * @returns {Observable<Array<CardType>>} An observable that emits the array of card types retrieved from the server.
   */
  getCardTypes(): Observable<Array<CardType>> {
    return this.get('paymentAccounts/cardTypes/', {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        return Deserialize(response, CardType);
      })
    );
  }

  /**
   * Find a card type by its id
   *
   * @param {number} id - The id of the card type to find
   * @return {Observable<CardType>} - An Observable that resolves to the card type with the specified id
   */
  findCardTypeById(id: number): Observable<CardType> {
    return this.getCardTypes().pipe(map(response => response.find(cardType => cardType.id === id)));
  }

  /**
   * Find a card type by name.
   *
   * @param {string} name - The name of the card type to search for.
   *
   * @return {Observable<CardType>} - An Observable that emits the card type with the specified name.
   */
  findCardTypeByName(name: string): Observable<CardType> {
    return this.getCardTypes().pipe(map(response => response.find(cardType => cardType.name === name)));
  }

  /**
   * Formats the expiration date in the specified format.
   *
   * @param {string} expirationDate - The expiration date to be formatted (in 'YYYY-MM' format)
   * @return {string} - The formatted expiration date as per the defined format
   */
  getFormattedExpirationDate(expirationDate: string) {
    return moment(expirationDate, 'YYYY-MM').format(DateFormatConstants.CARD_EXPIRATION);
  }

  /**
   * Check if a payment account has expired on a specific date.
   *
   * @param {PaymentAccount} paymentAccount - The payment account to check for expiration.
   * @param {Date} onDate - The date to check against the payment account expiration. Defaults to the current date.
   *
   * @return {boolean} - True if the payment account has expired on the given date, false otherwise.
   */
  isPaymentAccountExpired(paymentAccount: PaymentAccount, onDate: Date = new Date()): boolean {
    const expirationMoment = moment(paymentAccount.cardExpiration, 'YYYY-MM').endOf('month');
    return moment(onDate).isAfter(expirationMoment);
  }

  /**
   * Sorts an array of PaymentAccount objects by their charge order.
   *
   * @param {Array<PaymentAccount>} paymentAccounts - An array of PaymentAccount objects to be sorted.
   *
   * @return {Array<PaymentAccount>} - The sorted array of PaymentAccount objects.
   */
  sortPaymentAccountsByChargeOrder(paymentAccounts: Array<PaymentAccount>): Array<PaymentAccount> {
    return paymentAccounts.sort((a, b) => a.chargeOrder - b.chargeOrder);
  }
}
